import React from "react";
import { useAuth } from "../auth/AuthContext";

// 🔑 Bypass-flag (true => Everything allowed in UI) -- remove after version 2.14 when the role base access is stable
const allowAll = () => { return false; };

// ---- Base functions ----
export const hasRole = (roleIds, required) =>
  allowAll()
    ? true
    : (Array.isArray(required) ? required.every(id => roleIds.includes(id)) : roleIds.includes(required));

export const hasAnyRole = (roleIds, anyOf) =>
  allowAll()
    ? true
    : (Array.isArray(anyOf) ? anyOf.some(id => roleIds.includes(id)) : roleIds.includes(anyOf));

export const hasAllRoles = (roleIds, allOf) =>
  allowAll()
    ? true
    : (Array.isArray(allOf) ? allOf.every(id => roleIds.includes(id)) : roleIds.includes(allOf));

// ---- Render guard: shows children only if role exists ----
export function RequireRole({ anyOf, allOf, children, fallback = null }) {
  const { roleIds, isAuthLoading } = useAuth();
  if (isAuthLoading) return null;
  if (allowAll()) return <>{children}</>;           // ⬅️ bypass

  if (anyOf && !hasAnyRole(roleIds, anyOf)) return fallback;
  if (allOf && !hasAllRoles(roleIds, allOf)) return fallback;

  return <>{children}</>;
}

// ---- Render guard: hide if role is missing ----
export function HideIfNoRole({ anyOf, allOf, children }) {
  return (
    <RequireRole anyOf={anyOf} allOf={allOf} fallback={null}>
      {children}
    </RequireRole>
  );
}

// ---- Disable wrapper: passes disabled to child if role is missing ----
export function DisableIfNoRole({ anyOf, allOf, children }) {
  const { roleIds, isAuthLoading } = useAuth();
  if (isAuthLoading) return children ?? null;

  if (allowAll()) return children;                  // ⬅️ bypass (never disabled)

  const allowed =
    (anyOf ? hasAnyRole(roleIds, anyOf) : true) &&
    (allOf ? hasAllRoles(roleIds, allOf) : true);

  const child = React.Children.only(children);
  return React.cloneElement(child, { disabled: !allowed || child.props.disabled });
}

// ---- HOC ----
export function withRoleGuard(Wrapped, { anyOf, allOf, fallback = null }) {
  return function Guarded(props) {
    const { roleIds, isAuthLoading } = useAuth();
    if (isAuthLoading) return null;
    if (allowAll()) return <Wrapped {...props} />;  // ⬅️ bypass

    const allowed =
      (anyOf ? hasAnyRole(roleIds, anyOf) : true) &&
      (allOf ? hasAllRoles(roleIds, allOf) : true);

    return allowed ? <Wrapped {...props} /> : fallback;
  };
}

// ---- Route guard (React Router v6+) ----
export function ProtectedRoute({ anyOf, allOf, element, fallback = null }) {
  const { roleIds, isAuthLoading } = useAuth();
  if (isAuthLoading) return null;
  if (allowAll()) return element;                   // ⬅️ bypass

  const allowed =
    (anyOf ? hasAnyRole(roleIds, anyOf) : true) &&
    (allOf ? hasAllRoles(roleIds, allOf) : true);

  return allowed ? element : fallback;
}
